import React from 'react';
import { FlaskConical, TrendingUp, Gauge, TrendingDown, AlertTriangle, Info, RefreshCw, HelpCircle } from 'lucide-react';

const ScenariosTab = ({ 
  scenarios, 
  isGeneratingScenarios, 
  generateScenarios 
}) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-marine-cyan text-2xl uppercase tracking-wider flex items-center gap-3">
          <FlaskConical className="w-7 h-7" />
          Scénarios Prospectifs
        </h2>
        <button
          onClick={generateScenarios}
          disabled={isGeneratingScenarios}
          className="px-6 py-4 bg-marine-cyan/20 border-2 border-marine-cyan text-marine-cyan hover:bg-marine-cyan hover:text-black font-mono text-sm uppercase tracking-wider flex items-center gap-2 disabled:opacity-50"
          data-testid="generate-scenarios-btn"
        >
          {isGeneratingScenarios ? (
            <RefreshCw className="w-5 h-5 animate-spin" />
          ) : (
            <FlaskConical className="w-5 h-5" />
          )}
          Générer 4 Scénarios
        </button>
      </div>

      {/* EXPLICATION DÉTAILLÉE - SCÉNARIOS vs SIMU */}
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <HelpCircle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-mono text-amber-400 font-bold text-sm mb-2">
              SCÉNARIOS vs SIMU : Quelle différence ?
            </p>
            <div className="grid md:grid-cols-2 gap-4 text-xs font-mono">
              <div className="bg-black/30 p-3 rounded border border-cyan-500/20">
                <p className="text-cyan-400 font-bold mb-1">SCÉNARIOS (cet onglet)</p>
                <ul className="text-white/90 space-y-1">
                  <li>• Génère <strong className="text-white">4 hypothèses narratives</strong></li>
                  <li>• Basé sur <strong className="text-white">votre brief de mission</strong></li>
                  <li>• Types: Favorable / Médian / Défavorable / Catastrophe</li>
                  <li>• Analyse <strong className="text-white">qualitative</strong> avec probabilités</li>
                  <li>• Inclut déclencheurs et durée estimée</li>
                </ul>
              </div>
              <div className="bg-black/30 p-3 rounded border border-purple-500/20">
                <p className="text-purple-400 font-bold mb-1">SIMU (onglet Simu)</p>
                <ul className="text-white/90 space-y-1">
                  <li>• <strong className="text-white">Simulations Monte Carlo</strong> (500+ itérations)</li>
                  <li>• Templates de crises <strong className="text-white">prédéfinis</strong></li>
                  <li>• Résultats <strong className="text-white">quantitatifs</strong> : P10/P50/P90</li>
                  <li>• Impact chiffré sur Brent, TTF, réserves</li>
                  <li>• Calibré sur crises historiques</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Source des données */}
      <div className="bg-marine-slate/20 border border-marine-slate/50 p-4 rounded">
        <p className="font-mono text-white/80 text-sm mb-2">
          <strong className="text-marine-cyan">Comment sont générés les scénarios ?</strong>
        </p>
        <p className="font-mono text-white/90 text-sm">
          Les scénarios utilisent automatiquement : <br/>
          <strong className="text-white">1.</strong> Le <strong className="text-cyan-400">brief de mission</strong> saisi dans l'onglet "Brief" <br/>
          <strong className="text-white">2.</strong> Les <strong className="text-cyan-400">KPI actuels</strong> (prix pétrole, gaz, indicateurs de risque) <br/>
          <strong className="text-white">3.</strong> Les <strong className="text-cyan-400">données du scan OSINT</strong> si disponibles (optionnel mais améliore la précision)
        </p>
      </div>

      <p className="font-mono text-white/90 text-base">
        Cliquez sur <strong className="text-marine-cyan">"Générer 4 Scénarios"</strong> pour créer les hypothèses : 
        <strong className="text-marine-green"> Favorable</strong>, 
        <strong className="text-marine-amber"> Médian</strong>, 
        <strong className="text-marine-red"> Défavorable</strong>, et 
        <strong className="text-red-400"> Catastrophe</strong>.
        Ces scénarios seront automatiquement inclus dans le rapport PDF.
      </p>

      {scenarios.length > 0 ? (
        <div className="grid md:grid-cols-2 gap-4">
          {scenarios.map((scenario) => {
            const colorMap = {
              'favorable': { border: 'border-green-500', bg: 'bg-green-500/10', text: 'text-green-400', icon: TrendingUp },
              'median': { border: 'border-amber-500', bg: 'bg-amber-500/10', text: 'text-amber-400', icon: Gauge },
              'defavorable': { border: 'border-red-500', bg: 'bg-red-500/10', text: 'text-red-400', icon: TrendingDown },
              'catastrophe': { border: 'border-red-900', bg: 'bg-red-900/20', text: 'text-red-300', icon: AlertTriangle },
            };
            const style = colorMap[scenario.id] || colorMap['median'];
            const IconComponent = style.icon;

            return (
              <div key={scenario.id} className={`${style.bg} border-2 ${style.border} p-5`}>
                <div className="flex items-center gap-3 mb-4">
                  <IconComponent className={`w-6 h-6 ${style.text}`} />
                  <h3 className={`font-heading text-lg uppercase tracking-wider ${style.text}`}>
                    {scenario.name}
                  </h3>
                  <span className="ml-auto font-mono text-white/90 text-sm px-2 py-1 bg-black/50">
                    {Math.round(scenario.probability * 100)}%
                  </span>
                </div>
                
                <p className="font-mono text-white/90 text-sm mb-4">{scenario.description}</p>
                
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="bg-black/50 p-2">
                    <p className="font-mono text-white/80 text-xs uppercase">Brent</p>
                    <p className={`font-mono text-lg font-bold ${style.text}`}>
                      {scenario.projected_kpi?.brent_price}$
                    </p>
                  </div>
                  <div className="bg-black/50 p-2">
                    <p className="font-mono text-white/80 text-xs uppercase">Gaz</p>
                    <p className={`font-mono text-lg font-bold ${style.text}`}>
                      {scenario.projected_kpi?.gas_price}€
                    </p>
                  </div>
                  <div className="bg-black/50 p-2">
                    <p className="font-mono text-white/80 text-xs uppercase">Impact Pétrole</p>
                    <p className={`font-mono text-lg font-bold ${style.text}`}>
                      +{scenario.projected_kpi?.oil_impact_pct}%
                    </p>
                  </div>
                  <div className="bg-black/50 p-2">
                    <p className="font-mono text-white/80 text-xs uppercase">Réserves</p>
                    <p className={`font-mono text-lg font-bold ${style.text}`}>
                      {scenario.projected_kpi?.reserves_after}j
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="font-mono text-white/80 text-xs uppercase">Déclencheurs</p>
                  {scenario.triggers?.map((trigger, i) => (
                    <p key={i} className="font-mono text-white/90 text-sm">• {trigger}</p>
                  ))}
                </div>

                <div className="mt-3 pt-3 border-t border-white/10">
                  <p className="font-mono text-white/80 text-xs uppercase mb-1">Durée estimée</p>
                  <p className="font-mono text-white text-sm font-bold">{scenario.duration}</p>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-black border-2 border-marine-slate/50 p-8 text-center">
          <FlaskConical className="w-12 h-12 text-marine-slate/50 mx-auto mb-4" />
          <p className="font-mono text-white/90 text-base">
            Aucun scénario généré. Cliquez sur "Générer 4 Scénarios" pour simuler les hypothèses.
          </p>
        </div>
      )}

      {scenarios.length > 0 && (
        <div className="bg-marine-green/10 border border-marine-green/50 p-4 flex items-center gap-3">
          <Info className="w-5 h-5 text-marine-green" />
          <p className="font-mono text-white/80 text-sm">
            Ces scénarios seront automatiquement inclus dans le rapport PDF lors de sa génération.
          </p>
        </div>
      )}
    </div>
  );
};

export default ScenariosTab;
